/**
 * Training Categories Configuration
 * Centralizes training topics and competency levels
 */

export const TRAINING_CATEGORIES = {
  regulatory: {
    id: 'regulatory',
    label: 'Regulatory/STCW',
    topics: [
      { id: 'stcw_bst', name: 'STCW Basic Safety Training', certType: 'STCW_BST' },
      { id: 'stcw_fire', name: 'STCW Advanced Fire Fighting', certType: 'STCW_FIRE' },
      { id: 'stcw_medical', name: 'STCW Medical First Aid', certType: 'STCW_MEDICAL' },
      { id: 'stcw_survival', name: 'STCW Personal Survival Techniques', certType: 'STCW_PST' },
      { id: 'stcw_social', name: 'STCW Personal Safety & Social Responsibilities', certType: 'STCW_PSSR' },
      { id: 'gmdss', name: 'GMDSS Radio Operator', certType: 'GMDSS' }
    ]
  },
  safety: {
    id: 'safety',
    label: 'Safety',
    topics: [
      { id: 'fire_safety', name: 'Fire Safety Procedures' },
      { id: 'abandon_ship', name: 'Abandon Ship Procedures' },
      { id: 'mob_response', name: 'Man Overboard Response' },
      { id: 'enclosed_space', name: 'Enclosed Space Entry' },
      { id: 'ppe_usage', name: 'PPE Usage' },
      { id: 'hazmat', name: 'Hazardous Materials Handling' }
    ]
  },
  equipment: {
    id: 'equipment',
    label: 'Equipment Familiarisation',
    topics: [
      { id: 'liferaft', name: 'Liferaft Operation' },
      { id: 'lifejacket', name: 'Lifejacket Familiarisation' },
      { id: 'epirb_sart', name: 'EPIRB/SART Operation' },
      { id: 'fire_extinguisher', name: 'Fire Extinguisher Use' },
      { id: 'breathing_apparatus', name: 'Breathing Apparatus' },
      { id: 'rescue_boat', name: 'Rescue Boat Operations' }
    ]
  },
  operational: {
    id: 'operational',
    label: 'Operational',
    topics: [
      { id: 'bridge_procedures', name: 'Bridge Procedures' },
      { id: 'cargo_handling', name: 'Cargo Handling' },
      { id: 'mooring', name: 'Mooring Operations' },
      { id: 'navigation', name: 'Navigation' },
      { id: 'watchkeeping', name: 'Watchkeeping Duties' },
      { id: 'anchoring', name: 'Anchoring Procedures' }
    ]
  },
  company: {
    id: 'company',
    label: 'Company/SMS',
    topics: [
      { id: 'toolbox_talk', name: 'Toolbox Talk' },
      { id: 'safety_induction', name: 'Safety Induction' },
      { id: 'sms_familiarisation', name: 'SMS Familiarisation' },
      { id: 'near_miss_review', name: 'Near-Miss Review' },
      { id: 'emergency_procedures', name: 'Emergency Procedures Review' }
    ]
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    topics: []
  }
};

export const COMPETENCY_LEVELS = [
  { id: 'awareness', name: 'Awareness', description: 'Basic understanding of concepts' },
  { id: 'basic', name: 'Basic Understanding', description: 'Can perform with guidance' },
  { id: 'competent', name: 'Competent', description: 'Can perform independently' },
  { id: 'proficient', name: 'Proficient', description: 'Can perform and teach others' },
  { id: 'expert', name: 'Expert', description: 'Subject matter expert' }
];

// Helper to get all topics as flat array
export const getAllTopics = () => {
  return Object.values(TRAINING_CATEGORIES).flatMap(cat =>
    cat.topics.map(topic => ({ ...topic, category: cat.id, categoryLabel: cat.label }))
  );
};

// Helper to get topic by ID
export const getTopicById = (topicId) => {
  return getAllTopics().find(t => t.id === topicId);
};

// Helper to get category by ID
export const getCategoryById = (categoryId) => {
  return TRAINING_CATEGORIES[categoryId];
};

// STCW/WBC3 Certificate Types (same as crew profile)
export const STCW_CERTIFICATE_TYPES = [
  { id: 'stcw_bst', name: 'STCW Basic Safety Training (BST)', renewalYears: 5 },
  { id: 'stcw_pst', name: 'STCW Personal Survival Techniques', renewalYears: 5 },
  { id: 'stcw_fire_prevention', name: 'STCW Fire Prevention & Fire Fighting', renewalYears: 5 },
  { id: 'stcw_efa', name: 'STCW Elementary First Aid', renewalYears: 5 },
  { id: 'stcw_pssr', name: 'STCW Personal Safety & Social Responsibilities', renewalYears: 5 },
  { id: 'stcw_advanced_fire', name: 'STCW Advanced Fire Fighting', renewalYears: 5 },
  { id: 'stcw_medical_first_aid', name: 'STCW Medical First Aid', renewalYears: 5 },
  { id: 'stcw_medical_care', name: 'STCW Medical Care', renewalYears: 5 },
  { id: 'stcw_survival_craft', name: 'STCW Proficiency in Survival Craft', renewalYears: 5 },
  { id: 'stcw_fast_rescue', name: 'STCW Fast Rescue Boats', renewalYears: 5 },
  { id: 'stcw_ship_security', name: 'STCW Ship Security Officer', renewalYears: 5 },
  { id: 'stcw_security_awareness', name: 'STCW Security Awareness', renewalYears: -1 }, // No expiry
  { id: 'stcw_designated_security', name: 'STCW Designated Security Duties', renewalYears: -1 }, // No expiry
  { id: 'gmdss_goc', name: 'GMDSS General Operator Certificate', renewalYears: 5 },
  { id: 'gmdss_roc', name: 'GMDSS Restricted Operator Certificate', renewalYears: 5 },
];

// Helper to calculate certificate expiry date
export const calculateCertificateExpiry = (trainingDate, renewalYears = 5) => {
  if (renewalYears === -1) return null; // No expiry
  const date = new Date(trainingDate);
  date.setFullYear(date.getFullYear() + renewalYears);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD
};